import { generatePath } from '@cibo/core'
import { Loading } from '@cibo/profile'
import { Alert, Box, Container, Grid, MenuItem, Paper, Stack, Typography } from '@mui/material'
import { SnapshotDownloader, SnapshotDownloaderChild } from 'consts/Theme'
import csv from 'csvtojson'
import { StyledSelect, useInventory } from 'features/Inventories'
import { CarbonIntensityByInventoryArea } from 'features/Inventories/components/CarbonIntensityByInventoryArea'
import { CarbonIntensityBySource } from 'features/Inventories/components/CarbonIntensityBySource'
import { EmissionFactorsByBoundary } from 'features/Inventories/components/EmissionFactorsByBoundary'
import { EmissionsByCategory } from 'features/Inventories/components/EmissionsByCategory'
import { PracticeAdoptionByCrop } from 'features/Inventories/components/PracticeAdoptionByCrop'
import { useInventoryReport } from 'features/Inventories/queries/useInventoryReport'
import { InventoryCsvRow } from 'features/Inventories/types'
import { SCOPE_THREE_ROUTES } from 'navigation/types/scopeThreeRoutes'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { WaitingPage } from '../../../features/Inventories/components/WaitingPage'
import { Targets } from './Targets'

const CURRENT_YEAR = 2023

export const InventoryDashboard = () => {
  const { inventoryId } = useParams()
  const [refetchInterval, setRefetchInterval] = useState<number | false>(1000)
  const { data: inventory, isLoading, dataUpdatedAt, isFetching, isStale } = useInventory(
    inventoryId,
    {
      refetchInterval,
    }
  )

  const { t } = useTranslation('impact/InventoryDashboard')
  const {
    data: inventoryReport,
    error: reportFetchError,
    isLoading: reportLoading,
    dataUpdatedAt: reportDataUpdatedAt,
  } = useInventoryReport(inventoryId)
  const [csvResult, setCsvResult] = useState<InventoryCsvRow[]>()
  const [
    commodityCarbonIntensityByInventoryArea,
    setCommodityCarbonIntensityByInventoryArea,
  ] = useState<Commodity>('Corn')
  const [commodityCarbonIntensityBySource, setCommodityCarbonIntensityBySource] = useState<
    Commodity
  >('Corn')
  const [commodityPracticeAdoptionByCrop, setCommodityPracticeAdoptionByCrop] = useState<Commodity>(
    'Corn'
  )
  const [commodityEmissionsByCategory, setCommodityEmissionsByCategory] = useState<Commodity>(
    'Corn'
  )

  useEffect(() => {
    inventoryReport?.reportStr &&
      csv({ checkType: true })
        .fromString(inventoryReport.reportStr)
        .then(result => {
          setCsvResult(result)
        })
  }, [reportDataUpdatedAt])

  useEffect(() => {
    if (reportFetchError) {
      setRefetchInterval(false)
    }
  }, [reportFetchError])

  useEffect(() => {
    if (inventory?.item.data.ciJobResult?.status !== 'complete') {
      setRefetchInterval(1000)
    } else {
      setRefetchInterval(false)
    }
  }, [dataUpdatedAt])

  const graphFilename = useCallback(
    (key: string, context?: any) => `${inventory?.item.name}__${t(key, context)}`,
    [inventory?.item.name]
  )

  if (isLoading || reportLoading) {
    return <Loading large />
  }

  if (!isFetching && !isStale && !inventory?.item.data.years) {
    return <Navigate replace to={generatePath(SCOPE_THREE_ROUTES.SETUP, { inventoryId })} />
  }

  if (!inventory) {
    return (
      <Container>
        <Alert severity="error">{t('missingInventory')}</Alert>
      </Container>
    )
  }

  if (refetchInterval) {
    return <WaitingPage />
  }

  return (
    <Container>
      <Grid container spacing={3} paddingBottom={3}>
        <Grid item sm={12} md={4}>
          <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
            <Stack spacing={2} alignItems="start" p={3}>
              <Typography variant="h6" data-testid="inventoryTargets">
                {t('inventoryTargets')}
              </Typography>
              <Targets />
            </Stack>
          </Paper>
        </Grid>

        <Grid item sm={12} md={8}>
          <Stack spacing={3}>
            <SnapshotDownloader
              filename={graphFilename('carbonIntensityByInventoryArea')}
              buttonPosition={{ right: 160, top: 25 }}
            >
              {({ subjectRef }: SnapshotDownloaderChild) => (
                <Paper variant="outlined" ref={subjectRef} sx={{ borderRadius: '12px' }}>
                  <Stack p={3} spacing={3} direction="column">
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" data-testid="carbonIntensityByInventoryArea">
                        {t('carbonIntensityByInventoryArea')}
                      </Typography>
                      <CommoditySelector
                        value={commodityCarbonIntensityByInventoryArea}
                        setValue={setCommodityCarbonIntensityByInventoryArea}
                      />
                    </Stack>
                    {!!csvResult && (
                      <CarbonIntensityByInventoryArea
                        csvData={csvResult}
                        height={200}
                        commodity={commodityCarbonIntensityByInventoryArea}
                      />
                    )}
                  </Stack>
                </Paper>
              )}
            </SnapshotDownloader>

            <SnapshotDownloader
              filename={graphFilename('carbonIntensityBySource')}
              buttonPosition={{ right: 160, top: 25 }}
            >
              {({ subjectRef }: SnapshotDownloaderChild) => (
                <Paper ref={subjectRef} variant="outlined" sx={{ borderRadius: '12px' }}>
                  <Stack p={3} spacing={3} direction="column">
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" data-testid="carbonIntensityBySource">
                        {t('carbonIntensityBySource')}
                      </Typography>
                      <CommoditySelector
                        value={commodityCarbonIntensityBySource}
                        setValue={setCommodityCarbonIntensityBySource}
                      />
                    </Stack>
                    {!!csvResult && (
                      <Box>
                        <CarbonIntensityBySource
                          csvData={csvResult}
                          height={200}
                          commodity={commodityCarbonIntensityBySource}
                        />
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}
            </SnapshotDownloader>

            <SnapshotDownloader filename={graphFilename('emissionsFactors')}>
              {({ subjectRef }: SnapshotDownloaderChild) => (
                <Paper ref={subjectRef} variant="outlined" sx={{ borderRadius: '12px' }}>
                  <Stack p={3} spacing={3} direction="column">
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="h6" data-testid="emissionsFactors">
                        {t('emissionsFactors')}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {t('intensityKg')}
                      </Typography>
                    </Stack>
                    {!!csvResult && (
                      <EmissionFactorsByBoundary
                        csvData={csvResult}
                        height={200}
                        year={CURRENT_YEAR}
                      />
                    )}
                  </Stack>
                </Paper>
              )}
            </SnapshotDownloader>

            <SnapshotDownloader
              filename={graphFilename('practiceAdoption')}
              buttonPosition={{ right: 160, top: 25 }}
            >
              {({ subjectRef }: SnapshotDownloaderChild) => (
                <Paper ref={subjectRef} variant="outlined" sx={{ borderRadius: '12px' }}>
                  <Stack p={3} spacing={3} direction="column">
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" data-testid="practiceAdoption">
                        {t('practiceAdoption')}
                      </Typography>
                      <CommoditySelector
                        value={commodityPracticeAdoptionByCrop}
                        setValue={setCommodityPracticeAdoptionByCrop}
                      />
                    </Stack>
                    {!!csvResult && (
                      <PracticeAdoptionByCrop
                        year={CURRENT_YEAR}
                        csvData={csvResult}
                        commodity={commodityPracticeAdoptionByCrop}
                        height={200}
                      />
                    )}
                  </Stack>
                </Paper>
              )}
            </SnapshotDownloader>

            <SnapshotDownloader
              filename={graphFilename('interventionEmissions', { year: CURRENT_YEAR })}
              buttonPosition={{ right: 160, top: 25 }}
            >
              {({ subjectRef }: SnapshotDownloaderChild) => (
                <Paper ref={subjectRef} variant="outlined" sx={{ borderRadius: '12px' }}>
                  <Stack p={3} spacing={3} direction="column">
                    <Stack direction="column" spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" data-testid="interventionEmissions">
                          {t('interventionEmissions', { year: CURRENT_YEAR })}
                        </Typography>
                        <CommoditySelector
                          value={commodityEmissionsByCategory}
                          setValue={setCommodityEmissionsByCategory}
                        />
                      </Stack>
                      <Typography variant="body1" color="text.secondary">
                        {t('mTco2eq')}
                      </Typography>
                    </Stack>
                    {!!csvResult && (
                      <EmissionsByCategory
                        csvData={csvResult}
                        height={200}
                        commodity={commodityEmissionsByCategory}
                        year={CURRENT_YEAR}
                      />
                    )}
                  </Stack>
                </Paper>
              )}
            </SnapshotDownloader>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  )
}

type Commodity = 'Corn' | 'Soy'

const CommoditySelector = ({
  value,
  setValue,
}: {
  value: Commodity
  setValue: Dispatch<SetStateAction<Commodity>>
}) => {
  const { t: cropT } = useTranslation('@cibo/ui/cropNames')

  return (
    <StyledSelect
      value={value}
      onChange={({ target }) => setValue(target.value as Commodity)}
      sx={{
        width: 120,
      }}
    >
      <MenuItem value="Corn">{cropT('corn')}</MenuItem>
      <MenuItem value="Soy">{cropT('soybeans')}</MenuItem>
    </StyledSelect>
  )
}
